/*
 * component-product-image-zoom.js  (Local 4.1.1)
 *
 * Product-page image viewer built on PhotoSwipe v5 (self-hosted ESM).
 *
 * This filename is already loaded by sections/main-product.liquid (gated by
 * `enable_zoom`), so we reuse it instead of adding a new asset. It fully
 * replaces the old hand-rolled `#zoom` modal.
 *
 * Config is read from a global that the Liquid sets ONCE per product page:
 *   window.TZ_PSWP = { core, lightbox, css, viewer }  // asset URLs
 *
 * Markup contract (snippets/product-media.liquid):
 *   [data-product-single-media-wrapper]        gallery item wrapper
 *     [data-image-zoom]                         present on zoomable image wrappers
 *       data-image = full-res image URL
 *       data-index = 0-based position in the gallery
 *       data-w / data-h = intrinsic pixel dimensions
 *
 * FAIL SAFE: if window.TZ_PSWP is missing OR the dynamic import fails
 * (library not uploaded yet), we console.warn and return. We never throw and
 * never break the page; clicking the image just does nothing extra.
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Safe no-op custom element.
  // The Liquid still renders <product-image-zoom> for backwards compatibility.
  // Defining it as an inert element guarantees the markup never errors and the
  // old click-to-open behavior is disabled (the image wrapper is the trigger now).
  // ---------------------------------------------------------------------------
  try {
    if (typeof customElements !== 'undefined' && !customElements.get('product-image-zoom')) {
      customElements.define('product-image-zoom', class extends HTMLElement {});
    }
  } catch (e) {
    /* never throw from here */
  }

  // ---------------------------------------------------------------------------
  // One-time asset injection guards.
  // ---------------------------------------------------------------------------
  var stylesInjected = false;

  function injectStylesheet(href) {
    if (!href) return;
    // Avoid double-injecting the same href.
    if (document.querySelector('link[data-tz-pswp="' + href + '"]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-tz-pswp', href);
    document.head.appendChild(link);
  }

  function injectStylesOnce() {
    if (stylesInjected) return;
    var cfg = window.TZ_PSWP || {};
    injectStylesheet(cfg.css);     // PhotoSwipe core CSS
    injectStylesheet(cfg.viewer);  // our viewer overrides (thumb strip, close, etc.)
    stylesInjected = true;
  }

  // ---------------------------------------------------------------------------
  // Derive a small thumbnail URL from a Shopify CDN image URL by appending a
  // width param. Works whether or not the URL already has a query string.
  // ---------------------------------------------------------------------------
  function thumbUrl(src, width) {
    if (!src) return src;
    var w = width || 160;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  // ---------------------------------------------------------------------------
  // Collect the ordered dataSource for one gallery.
  // Reads full-res URL + dimensions from the wrapper element's data-* attrs.
  // ---------------------------------------------------------------------------
  function buildDataSource(items) {
    return items.map(function (el) {
      var src = el.getAttribute('data-image') || '';
      var w = parseInt(el.getAttribute('data-w'), 10);
      var h = parseInt(el.getAttribute('data-h'), 10);
      var slide = { src: src };
      // width/height let PhotoSwipe size & zoom correctly before load.
      if (w > 0) slide.width = w;
      if (h > 0) slide.height = h;
      slide.thumb = thumbUrl(src, 160); // used by the custom bottom strip
      return slide;
    });
  }

  // ---------------------------------------------------------------------------
  // De-duplicate gallery items by media identity. The theme's <css-slider>
  // can CLONE slides (e.g. for looping), so the same media may appear more
  // than once in the DOM; without dedupe the viewer would show duplicates.
  // ---------------------------------------------------------------------------
  function mediaKey(el) {
    return el.getAttribute('data-media-id') || el.getAttribute('data-image') || '';
  }

  function uniqueByMedia(list) {
    var seen = {};
    var out = [];
    list.forEach(function (el) {
      var key = mediaKey(el);
      if (key && seen[key]) return;
      seen[key] = true;
      out.push(el);
    });
    return out;
  }

  // ---------------------------------------------------------------------------
  // Sort gallery items by their data-index so lightbox order matches the page.
  // ---------------------------------------------------------------------------
  function sortByIndex(list) {
    return list.slice().sort(function (a, b) {
      var ai = parseInt(a.getAttribute('data-index'), 10);
      var bi = parseInt(b.getAttribute('data-index'), 10);
      if (isNaN(ai)) ai = 0;
      if (isNaN(bi)) bi = 0;
      return ai - bi;
    });
  }

  // ---------------------------------------------------------------------------
  // Build the bottom thumbnail strip (Kettl-style) as a PhotoSwipe UI element,
  // keep the active thumb outlined, and wire clicks -> pswp.goTo(i).
  // ---------------------------------------------------------------------------
  function registerThumbStrip(lightbox, dataSource) {
    lightbox.on('uiRegister', function () {
      var pswp = lightbox.pswp;
      if (!pswp || !pswp.ui) return;

      pswp.ui.registerElement({
        name: 'tz-thumbstrip',
        order: 9,                 // above the default toolbar area
        isButton: false,
        appendTo: 'wrapper',      // sits inside the pswp scroll wrapper, fixed via CSS
        onInit: function (el) {
          el.className = 'pswp__tz-thumbs';

          var buttons = [];

          dataSource.forEach(function (slide, i) {
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pswp__tz-thumb';
            btn.setAttribute('aria-label', 'Go to image ' + (i + 1));

            var img = document.createElement('img');
            img.src = slide.thumb || slide.src;
            img.alt = '';
            img.loading = 'lazy';
            img.decoding = 'async';
            btn.appendChild(img);

            btn.addEventListener('click', function () {
              if (pswp && typeof pswp.goTo === 'function') pswp.goTo(i);
            });

            buttons.push(btn);
            el.appendChild(btn);
          });

          // Sync the active thumb with the current slide.
          function setActive(index) {
            for (var i = 0; i < buttons.length; i++) {
              if (i === index) {
                buttons[i].classList.add('is-active');
              } else {
                buttons[i].classList.remove('is-active');
              }
            }
            // Keep the active thumb visible, but ONLY when the strip actually
            // overflows, and NEVER via scrollIntoView: scrollIntoView scrolls
            // every scrollable ancestor (on iOS that includes the PhotoSwipe
            // root), which panned the whole viewer sideways on mobile -- image
            // off-center, slides bleeding in, close button drifting. Instead,
            // set scrollLeft on the strip itself; nothing else can move.
            if (el.scrollWidth > el.clientWidth + 1) {
              var active = buttons[index];
              if (active) {
                var target = active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2;
                if (target < 0) target = 0;
                try {
                  el.scrollTo({ left: target, behavior: 'smooth' });
                } catch (e) {
                  el.scrollLeft = target;
                }
              }
            }
          }

          setActive(pswp.currIndex);
          pswp.on('change', function () {
            setActive(pswp.currIndex);
          });
        }
      });
    });
  }

  // ---------------------------------------------------------------------------
  // Open the viewer for a single gallery. Loads PhotoSwipe lazily on first click.
  // We keep one lightbox instance per gallery, created on demand.
  // ---------------------------------------------------------------------------
  function attachGallery(wrapper) {
    if (!wrapper || wrapper.__tzPswpBound) return;

    // Only image wrappers that opted into zoom (deduped: sliders clone slides).
    var items = uniqueByMedia(sortByIndex(
      Array.prototype.slice.call(wrapper.querySelectorAll('[data-image-zoom][data-image]'))
    ));
    if (!items.length) return;

    wrapper.__tzPswpBound = true;

    var dataSource = buildDataSource(items);
    var lightbox = null;          // lazily created PhotoSwipeLightbox instance
    var creating = false;         // guards against double init while importing

    // Create (once) and open at `index`.
    function openAt(index) {
      var cfg = window.TZ_PSWP;
      if (!cfg || !cfg.lightbox || !cfg.core) {
        console.warn('[product-image-zoom] window.TZ_PSWP is missing; PhotoSwipe not configured.');
        return;
      }

      // Already created -> just open.
      if (lightbox) {
        lightbox.loadAndOpen(index, dataSource);
        return;
      }
      if (creating) return;
      creating = true;

      injectStylesOnce();

      import(cfg.lightbox)
        .then(function (mod) {
          var PhotoSwipeLightbox = mod && mod.default ? mod.default : mod;

          lightbox = new PhotoSwipeLightbox({
            dataSource: dataSource,
            pswpModule: function () { return import(cfg.core); },
            // --- PhotoSwipe v5 options ---
            showHideAnimationType: 'zoom', // zoom in/out from the opener rect
            bgOpacity: 1,                  // opaque backdrop (color set in CSS)
            // wheelToZoom OFF: two-finger trackpad scroll PANS around the
            // (zoomed) image; pinch (reported as ctrl+wheel) still zooms.
            wheelToZoom: false,
            // Clicking the image only toggles zoom -- never closes the viewer
            // (the default 'zoom-or-close' closed it on images already at
            // full size, e.g. the Chrysanthemum Kyo starter set). Exit is via
            // the X, Esc, or clicking the backdrop.
            imageClickAction: 'zoom',
            arrowPrev: true,
            arrowNext: true,
            close: true,
            zoom: true,                    // zoom toggle button + double-tap/click zoom
            counter: false,                // no "1/x" in the corner; thumbs show position
            loop: true,
            // Reserve space for the thumb strip: taller on mobile where the
            // thumbs are larger (see component-product-image-viewer.css).
            paddingFn: function (viewportSize) {
              var mobile = viewportSize.x <= 767;
              return {
                top: 20,
                bottom: mobile ? 108 : 96,
                left: mobile ? 12 : 20,
                right: mobile ? 12 : 20
              };
            }
          });

          // Mark the document while the viewer is open so CSS can suppress
          // third-party floating widgets (e.g. the rewards launcher) that
          // otherwise overlap the thumbnail strip on mobile.
          lightbox.on('beforeOpen', function () {
            document.documentElement.classList.add('tz-pswp-open');
            if (lightbox.pswp) {
              lightbox.pswp.on('destroy', function () {
                document.documentElement.classList.remove('tz-pswp-open');
              });
            }
          });

          registerThumbStrip(lightbox, dataSource);

          lightbox.init();
          creating = false;
          lightbox.loadAndOpen(index, dataSource);
        })
        .catch(function (err) {
          creating = false;
          // Library not uploaded yet or failed to load: fail safe.
          console.warn('[product-image-zoom] PhotoSwipe failed to load; viewer disabled.', err);
        });
    }

    // Delegate clicks from the gallery container instead of binding per item.
    // The theme's <css-slider> rebuilds the gallery subtree via innerHTML
    // (immediately for "slider" galleries, and below 1024px for "scroll"
    // galleries), which destroys per-item listeners. The container itself
    // survives the rebuild, so one delegated listener covers all cases --
    // desktop, mobile, and any future DOM rebuilds. Items are re-queried at
    // click time so the dataSource always reflects the live DOM.
    // (The zoom-in hover cursor comes from component-product-image-viewer.css,
    // which is loaded eagerly by snippets/product-media.liquid.)
    wrapper.addEventListener('click', function (evt) {
      var el = evt.target && evt.target.closest
        ? evt.target.closest('[data-image-zoom][data-image]')
        : null;
      if (!el || !wrapper.contains(el)) return;
      evt.preventDefault();
      var liveItems = uniqueByMedia(sortByIndex(
        Array.prototype.slice.call(wrapper.querySelectorAll('[data-image-zoom][data-image]'))
      ));
      if (liveItems.length) {
        items = liveItems;
        dataSource = buildDataSource(liveItems);
      }
      // Map the clicked element (possibly a slider CLONE not present in the
      // deduped list) onto its slide via media identity, not element identity.
      var pos = items.indexOf(el);
      if (pos < 0) {
        var key = mediaKey(el);
        for (var i = 0; i < items.length; i++) {
          if (mediaKey(items[i]) === key) { pos = i; break; }
        }
      }
      if (pos < 0) {
        var idx = parseInt(el.getAttribute('data-index'), 10);
        pos = isNaN(idx) ? 0 : idx;
      }
      openAt(pos);
    });
  }

  // ---------------------------------------------------------------------------
  // Scan the document (or a root) for product galleries and wire them up.
  // A "gallery" is any element that contains zoomable image wrappers; we scope
  // to the nearest product gallery container when possible, else the document.
  // ---------------------------------------------------------------------------
  function initAll(root) {
    var scope = root || document;

    // Preferred: one lightbox per product gallery container.
    var galleries = scope.querySelectorAll('[data-js-product-gallery]');
    if (galleries.length) {
      Array.prototype.forEach.call(galleries, function (g) { attachGallery(g); });
      return;
    }

    // Fallback: if the container isn't found, treat the whole scope as one gallery
    // (only if it actually holds zoomable items).
    if (scope.querySelector && scope.querySelector('[data-image-zoom][data-image]')) {
      attachGallery(scope.nodeType === 1 ? scope : document.body);
    }
  }

  // ---------------------------------------------------------------------------
  // Boot.
  // ---------------------------------------------------------------------------
  function boot() {
    try {
      initAll(document);
    } catch (e) {
      console.warn('[product-image-zoom] init failed (page not broken).', e);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run on Shopify theme-editor section render (defensive; ignores errors).
  document.addEventListener('shopify:section:load', function (evt) {
    try {
      var target = (evt && evt.target) || document;
      initAll(target);
    } catch (e) {
      /* no-op */
    }
  });
})();
